import {
    type FocusEvent,
    type KeyboardEvent,
    useCallback,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cssClasses from 'classnames';

import CalendarLine from '@/assets/icons/calendar_line.svg?component';
import { joinIds, useFormFieldControl } from '@/components/atoms/FormField/FormFieldContext';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useClickOutside } from '@/hooks/useClickOutside';
import { parseLocale } from '@/utils/localeFormatters';

import { PickerCalendar } from './PickerCalendar';
import { atNoon, type DayBounds } from './pickerDays';
import { useTypedDay } from './useTypedDay';

import styles from './StyledSingleDatePicker.module.scss';

interface StyledSingleDatePickerProps {
    id?: string;
    date?: Date | null;
    placeholder?: string;
    required?: boolean;
    useOutsideRange?: boolean;
    blockFutureDates?: boolean;
    /** A day typed before it is dropped: `onDateChange` gets null. */
    minDate?: Date | null;
    maxDate?: Date | null;
    /**
     * Days before it can't be picked from the calendar, but a typed earlier day still reaches
     * `onDateChange`, so the form can say why it refuses it instead of reading it as empty.
     */
    blockDaysBefore?: Date | null;
    onDateChange: (date: Date | null) => void;
    /**
     * Every keystroke's text with the day it reads as, or null. `onDateChange` reports null
     * for an emptied field and for text that is not a day alike; a form that must not take
     * a typo for a clear tells them apart here.
     */
    onTextChange?: (text: string, day: Date | null) => void;
}

/**
 * A day field: type `dd/MM/yyyy`, or pick from the calendar that opens under it on focus.
 * Days come back at local noon. ArrowDown moves into the calendar, Escape closes it.
 */
export default function StyledSingleDatePicker(props: StyledSingleDatePickerProps) {
    const {
        id,
        required = false,
        placeholder = '',
        date = null,
        useOutsideRange = false,
        blockFutureDates = false,
        minDate = null,
        maxDate = null,
        blockDaysBefore = null,
        onDateChange,
        onTextChange,
    } = props;

    const { t, i18n } = useTranslation('atoms');
    const locale = parseLocale(i18n.language);
    // Inside a FormField the field's id ties its label to the date input.
    const field = useFormFieldControl({ id });
    // useId's colons are valid in an id but not in a CSS selector, which jsdom and tests use.
    const generatedId = useId().replace(/:/g, '');
    const inputId = field.id ?? generatedId;
    const calendarId = `${generatedId}-calendar`;
    const hintId = `${generatedId}-hint`;

    const [open, setOpen] = useState(false);
    const [focusCalendar, setFocusCalendar] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // Focus handed back to the input after a pick or Escape must not reopen the calendar.
    const quietFocus = useRef(false);

    const bounds = useMemo<DayBounds>(
        () => ({ minDate, maxDate, blockFutureDates, useOutsideRange }),
        [minDate, maxDate, blockFutureDates, useOutsideRange]
    );
    // The calendar also blocks the days before `blockDaysBefore`; typing goes by `bounds`.
    const calendarBounds = useMemo<DayBounds>(
        () =>
            blockDaysBefore && (!minDate || blockDaysBefore > minDate)
                ? { ...bounds, minDate: blockDaysBefore }
                : bounds,
        [bounds, blockDaysBefore, minDate]
    );

    const openCalendar = useCallback((withFocus: boolean) => {
        setFocusCalendar(withFocus);
        setOpen(true);
    }, []);
    const close = useCallback(() => setOpen(false), []);
    const typed = useTypedDay(date, locale, bounds, onDateChange, close, onTextChange);
    const { reset } = typed;

    // Leaving the field drops a half-typed day for the day it holds; with no day the typed
    // text stays to be corrected.
    const dismiss = useCallback(() => {
        if (!open) return;
        setOpen(false);
        if (date !== null) reset();
    }, [open, date, reset]);
    const closeToInput = useCallback(() => {
        dismiss();
        quietFocus.current = true;
        inputRef.current?.focus();
    }, [dismiss]);

    useClickOutside(wrapperRef, dismiss);

    const handleSelect = useCallback(
        (day: Date) => {
            reset();
            onDateChange(atNoon(day));
            closeToInput();
        },
        [reset, onDateChange, closeToInput]
    );

    const handleInputFocus = useCallback(() => {
        if (quietFocus.current) {
            quietFocus.current = false;
            return;
        }
        openCalendar(false);
    }, [openCalendar]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Escape' && open) {
                // The calendar closes first; a menu or modal around it stays open.
                event.stopPropagation();
                closeToInput();
            } else if (event.key === 'ArrowDown' && event.target === inputRef.current) {
                event.preventDefault();
                openCalendar(true);
            }
        },
        [open, closeToInput, openCalendar]
    );

    // Tabbing out of the field closes the calendar; a click inside it moves focus nowhere.
    // The close waits for focus to land: removing the focused calendar button while focus
    // is still on its way makes a modal's focus trap take it back to the dialog itself.
    const handleBlur = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            const next = event.relatedTarget;
            if (next instanceof Node && !event.currentTarget.contains(next)) {
                window.setTimeout(dismiss, 0);
            }
        },
        [dismiss]
    );

    return (
        <div
            ref={wrapperRef}
            className={cssClasses(
                styles.singleDatePickerWrapper,
                open && styles.focused,
                field['aria-invalid'] && styles.invalid
            )}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}>
            <div className={styles.inputRow}>
                <input
                    ref={inputRef}
                    id={inputId}
                    type="text"
                    className={styles.input}
                    value={typed.text}
                    onChange={typed.onChange}
                    onFocus={handleInputFocus}
                    onClick={() => openCalendar(false)}
                    placeholder={placeholder || t('styledSingleDatePicker.placeholder')}
                    required={required}
                    autoComplete="off"
                    aria-invalid={field['aria-invalid']}
                    aria-describedby={joinIds(field['aria-describedby'], hintId)}
                />
                <button
                    type="button"
                    className={styles.calendarButton}
                    aria-label={t('styledSingleDatePicker.openCalendar')}
                    aria-expanded={open}
                    aria-controls={open ? calendarId : undefined}
                    onClick={() => (open ? dismiss() : openCalendar(true))}>
                    <SvgIcon icon={CalendarLine} />
                </button>
                <span id={hintId} className={styles.srOnly}>
                    {t('styledSingleDatePicker.keyboardHint')}
                </span>
            </div>
            {open && (
                <PickerCalendar
                    // Asking for focus while it is open remounts it, which moves focus in.
                    key={focusCalendar ? 'focused' : 'browsing'}
                    id={calendarId}
                    label={t('styledSingleDatePicker.calendarLabel')}
                    date={date}
                    locale={locale}
                    bounds={calendarBounds}
                    autoFocus={focusCalendar}
                    onSelect={handleSelect}
                />
            )}
        </div>
    );
}
